import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { normaliseWebsite, type NoteSource } from '@crm/contracts';
import { Link } from '@tanstack/react-router';
import { useMemo, useRef, useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type ProjectPatchBody, type ProjectView } from '../../lib/client.ts';
import {
  projectJobsQuery,
  projectQuery,
  useMembers,
  usePipeline,
  useProjectRoles,
} from '../../lib/record-data.ts';
import { useSession } from '../../lib/session.tsx';
import { LeveKpis } from '../leve/leve-kpis.tsx';
import { ProjectLeve } from '../leve/project-leve.tsx';
import { RecordMessages } from '../messages/record-messages.tsx';
import { ScheduledMessages } from '../messages/scheduled-messages.tsx';
import { ActivityTimeline } from '../records/activity-timeline.tsx';
import { NotesTasksBlock } from '../records/notes-tasks-block.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs.tsx';
import { ContactsTab } from './contacts-tab.tsx';
import { DealsTab } from './deals-tab.tsx';
import { AddJobForm, JobsTable } from './jobs-table.tsx';
import { ProjectCustomFields } from './project-custom-fields.tsx';
import { ProjectDescription } from './project-description.tsx';
import { ProjectHistory } from './project-history.tsx';
import { ProjectPipelines } from './project-pipelines.tsx';

/**
 * One project, start to finish (RELAY-952): the header has the description under the name (two
 * lines, "See more", edited in a dialog), the project functions beside the creation date (the
 * owner is not shown: it still grants access, but who works the client is the functions), and
 * shows the company's details; under it the Pipelines card has a stage bar for every pipeline the
 * project is in (TS-1748: move in one, add to another, leave one, archive a lost one); the tabs
 * hold the overview — ending with the notes (the project's and its related records', each
 * badged with where it is, a toggle by origin, and "Add note" picking the record) and the tasks,
 * not in a sidebar
 * beside it, so the page has its full width — its contacts, its deals (each expanding to its
 * jobs), every job (each expanding to its shortlists), the activity and the history.
 * Everything a project goes through, from lead to paid deals and filled jobs, happens on this
 * page.
 */
export function ProjectDetail({
  project,
  focusJobId,
  initialTab,
  openConversation,
}: {
  project: ProjectView;
  /** From the Jobs page (RELAY-953): open the Jobs tab with this job expanded and in view. */
  focusJobId?: string | undefined;
  /** From the Deals page (RELAY-1038): open on the Deals tab; from Home: on the Messages tab. */
  initialTab?: 'deals' | 'messages' | undefined;
  /** From Home's Pending messages: the text conversation to open on the Messages tab. */
  openConversation?: string | undefined;
}) {
  const session = useSession();
  const qc = useQueryClient();
  // Message Hub's texts and emails with the project's contacts (TS-1898), behind its flag.
  const messagesOn = session.isEnabled('messages.enabled') && session.can('customer.read');
  const [tab, setTab] = useState<string>(
    focusJobId
      ? 'jobs'
      : initialTab === 'messages' && !messagesOn
        ? 'overview'
        : (initialTab ?? 'overview'),
  );
  // The height of the tab left behind, held by a tab that loads (Finance), so the page does not
  // shrink to a one-line "loading" and scroll the reader up while it reads.
  const tabsRef = useRef<HTMLDivElement>(null);
  const [heldHeight, setHeldHeight] = useState(0);
  const changeTab = (next: string) => {
    const panel = tabsRef.current?.querySelector('[role="tabpanel"][data-state="active"]');
    setHeldHeight(panel?.getBoundingClientRect().height ?? 0);
    setTab(next);
  };
  const [error, setError] = useState<string | null>(null);
  const dealPipeline = usePipeline('deal');
  const jobPipeline = usePipeline('job');
  const shortlistPipeline = usePipeline('shortlist');
  const jobsOn = session.isEnabled('jobs.enabled') && session.can('job.read');
  // The client's Leve payments and fees & billing, read from Leve, behind its flag.
  const leveOn = session.isEnabled('leve.enabled') && session.can('customer.read');
  // Whoever holds one of its functions (Settings → Projects) counts as an owner, as in the API.
  const canWrite = session.can('customer.update', {
    ownerId: project.ownerId,
    responsibleIds: project.responsibles.map((r) => r.userId),
  });
  const canWriteJobs = session.can('job.update');

  const contacts = useQuery({
    queryKey: ['project-contacts', project.id],
    queryFn: () => api.projects.contacts(project.id),
  });
  const deals = useQuery({
    queryKey: ['project-deals', project.id],
    queryFn: () => api.projects.deals(project.id),
  });
  const jobs = useQuery({
    queryKey: projectJobsQuery(project.id),
    queryFn: () => api.jobs.ofProject(project.id),
    enabled: jobsOn,
  });
  const jobList = useMemo(() => jobs.data?.items ?? [], [jobs.data]);
  const labels = useMemo(
    () =>
      new Map<string, string>([
        ...(deals.data ?? []).map((d) => [d.id, d.title] as const),
        ...jobList.map((j) => [j.id, j.title] as const),
      ]),
    [deals.data, jobList],
  );
  // Whose notes the Overview lists, and where "Add note" may put one (`GET /notes/sources`).
  const noteSources = useQuery({
    queryKey: ['note-sources', 'projects', project.id],
    queryFn: () => api.notes.sources('projects', project.id),
  });
  const contactNames = useMemo(
    () => new Map((contacts.data ?? []).map((c) => [c.contact.id, c.contact.name] as const)),
    [contacts.data],
  );

  /** After any change: the project, its parts, its history and its notes are re-read. */
  const refresh = async () => {
    await Promise.all(
      [
        projectQuery(project.id),
        ['project-contacts', project.id],
        ['project-deals', project.id],
        projectJobsQuery(project.id),
        ['project-history', project.id],
        ['job-shortlists'],
        // A job's filled positions are its bundle's used slots (TS-925).
        ['bundles'],
        ['activities', 'projects', project.id],
        ['note-sources', 'projects', project.id],
      ].map((queryKey) => qc.invalidateQueries({ queryKey })),
    );
  };
  const patch = useMutation({
    mutationFn: (body: ProjectPatchBody) => api.projects.patch(project.id, body),
    onSuccess: async () => {
      setError(null);
      await refresh();
    },
    onError: (err) => setError(describeError(err)),
  });
  const setResponsible = useMutation({
    mutationFn: (v: { roleId: string; userId: string | null }) =>
      api.projects.setResponsible(project.id, v.roleId, v.userId),
    onSuccess: async () => {
      setError(null);
      await refresh();
    },
    onError: (err) => setError(describeError(err)),
  });

  return (
    <div data-testid="project-detail">
      <div className="flex min-w-0 flex-col gap-4">
        <header className="bg-card border-border rounded-card shadow-card border p-5 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <h2 className="text-2xl font-semibold tracking-tight break-words">{project.name}</h2>
            <ProjectDescription
              description={project.description}
              canWrite={canWrite}
              saving={patch.isPending}
              onSave={(description) =>
                patch.mutateAsync({ description }).then(
                  () => true,
                  // Shown by the mutation's onError; the dialog keeps the draft.
                  () => false,
                )
              }
            />
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <ResponsibleFields
              project={project}
              canWrite={canWrite}
              onChange={(roleId, userId) => setResponsible.mutate({ roleId, userId })}
            />
            <span className="text-muted-foreground" data-testid="project-created">
              Created{' '}
              <span className="text-foreground">
                {new Date(project.createdAt).toLocaleDateString()}
              </span>
            </span>
          </div>
          <CompanyInfo company={project.company} />
          {error && (
            <p className="text-danger text-sm" role="alert">
              {error}
            </p>
          )}
        </header>
        {/* Leve's KPI strip for this client, its own card under the header, on every tab. */}
        {leveOn && <LeveKpis projectId={project.id} />}
        <ProjectPipelines project={project} canWrite={canWrite} onChanged={refresh} />

        <Tabs ref={tabsRef} value={tab} onValueChange={changeTab}>
          {/* One row that scrolls sideways when narrow (the shared `TabsList`): wrapped, eight
              tabs broke into two or three rows on a phone or tablet. */}
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="contacts">
              Contacts{contacts.data ? ` (${contacts.data.length})` : ''}
            </TabsTrigger>
            <TabsTrigger value="deals">
              Deals{deals.data ? ` (${deals.data.length})` : ''}
            </TabsTrigger>
            {jobsOn && (
              <TabsTrigger value="jobs">Jobs{jobs.data ? ` (${jobList.length})` : ''}</TabsTrigger>
            )}
            {messagesOn && <TabsTrigger value="messages">Messages</TabsTrigger>}
            {leveOn && <TabsTrigger value="finance">Finance</TabsTrigger>}
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4">
            <Overview project={project} />
            <NotesTasksFields
              project={project}
              canWrite={canWrite}
              // Empty until loaded, so the card never flashes the inline form.
              noteSources={noteSources.data ?? []}
              onSaved={refresh}
            />
          </TabsContent>
          <TabsContent value="contacts" className="mt-4">
            <ContactsTab
              projectId={project.id}
              companyId={project.companyId}
              contacts={contacts.data ?? []}
              canWrite={canWrite}
              onChanged={refresh}
            />
          </TabsContent>
          <TabsContent value="deals" className="mt-4">
            <DealsTab
              projectId={project.id}
              deals={deals.data ?? []}
              jobs={jobList}
              dealPipeline={dealPipeline}
              jobPipeline={jobPipeline}
              shortlistPipeline={shortlistPipeline}
              jobsOn={jobsOn}
              canWrite={canWrite}
              canWriteJobs={canWriteJobs}
              canCreateJobs={session.can('job.create')}
              onChanged={refresh}
            />
          </TabsContent>
          {jobsOn && (
            <TabsContent value="jobs" className="mt-4 flex flex-col gap-3">
              <JobsTable
                jobs={jobList}
                pipeline={jobPipeline}
                shortlistPipeline={shortlistPipeline}
                canWrite={canWriteJobs}
                onChanged={refresh}
                focusJobId={focusJobId}
              />
              {session.can('job.create') && (
                <AddJobForm deals={deals.data ?? []} onCreated={refresh} />
              )}
            </TabsContent>
          )}
          {messagesOn && (
            <TabsContent value="messages" className="mt-4">
              {tab === 'messages' && (
                <div className="flex flex-col gap-4">
                  <ScheduledMessages
                    entityType="projects"
                    entityId={project.id}
                    canWrite={canWrite}
                  />
                  <RecordMessages
                    kind="projects"
                    id={project.id}
                    contactNames={contactNames}
                    openConversation={openConversation}
                  />
                </div>
              )}
            </TabsContent>
          )}
          {leveOn && (
            <TabsContent value="finance" className="mt-4">
              {tab === 'finance' && <ProjectLeve projectId={project.id} holdHeight={heldHeight} />}
            </TabsContent>
          )}
          <TabsContent value="activity" className="mt-4">
            {tab === 'activity' && <ActivityTimeline entityType="projects" entityId={project.id} />}
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            {tab === 'history' && <ProjectHistory projectId={project.id} labels={labels} />}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/** The project's notes and tasks block, then its custom fields (Overview tab). */
function NotesTasksFields({
  project,
  canWrite,
  noteSources,
  onSaved,
}: {
  project: ProjectView;
  canWrite: boolean;
  noteSources: readonly NoteSource[] | undefined;
  onSaved: () => Promise<void>;
}) {
  return (
    <>
      <NotesTasksBlock
        entityType="projects"
        entityId={project.id}
        canWrite={canWrite}
        sources={noteSources}
        kindFilter
      />
      <div className="mt-5">
        <ProjectCustomFields project={project} canWrite={canWrite} onSaved={onSaved} />
      </div>
    </>
  );
}

/** The project's company, read-only; it is edited on the Companies page. */
function CompanyInfo({ company: ref }: { company: ProjectView['company'] }) {
  const company = useQuery({
    queryKey: ['company', ref.id],
    queryFn: () => api.companies.get(ref.id),
  });
  const c = company.data;
  const name = c?.name ?? ref.name;
  // Rows saved before websites were validated may hold something unlinkable.
  const href = normaliseWebsite(c?.website);
  return (
    <section
      className="border-border flex flex-col gap-3 border-t pt-4"
      data-testid="project-company"
    >
      <p className="text-muted-foreground text-xs font-bold tracking-[0.08em] uppercase">Company</p>
      <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <div className="min-w-0">
          <dt className="text-muted-foreground">Name</dt>
          <dd className="truncate font-medium">
            {/* Out of the caller's scope, the panel would only say "No such company". */}
            {company.isError ? (
              name
            ) : (
              <Link
                to="/companies"
                search={{ open: ref.id }}
                target="_blank"
                rel="noreferrer"
                className="hover:underline"
              >
                {name}
              </Link>
            )}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-muted-foreground">Website</dt>
          <dd className="truncate">
            {href ? (
              <a href={href} target="_blank" rel="noreferrer" className="hover:underline">
                {c?.website}
              </a>
            ) : (
              c?.website || '—'
            )}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-muted-foreground">Industry</dt>
          <dd className="truncate">{c?.industry || '—'}</dd>
        </div>
      </dl>
    </section>
  );
}

const NOBODY = 'nobody';

/**
 * One picker per project function (Settings → Projects) in the header: who answers for the
 * project in that capacity. Read-only without write access; a function nobody holds reads "—".
 */
function ResponsibleFields({
  project,
  canWrite,
  onChange,
}: {
  project: ProjectView;
  canWrite: boolean;
  onChange: (roleId: string, userId: string | null) => void;
}) {
  const roles = useProjectRoles();
  const { members, nameOf } = useMembers();
  const holder = (roleId: string) =>
    project.responsibles.find((r) => r.roleId === roleId)?.userId ?? null;
  return (
    <>
      {roles.map((role) =>
        !canWrite ? (
          <span key={role.id} className="text-muted-foreground">
            {role.name}: <span data-testid="project-responsible">{nameOf(holder(role.id))}</span>
          </span>
        ) : (
          <label key={role.id} className="text-muted-foreground flex min-w-0 items-center gap-2">
            {role.name}
            <Select
              value={holder(role.id) ?? NOBODY}
              onValueChange={(v) => onChange(role.id, v === NOBODY ? null : v)}
            >
              <SelectTrigger className="h-9 w-52 max-w-full min-w-0" aria-label={role.name}>
                <SelectValue placeholder="Nobody" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NOBODY}>Nobody</SelectItem>
                {members
                  .filter((m) => m.status === 'active' || m.id === holder(role.id))
                  .map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name || m.email}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </label>
        ),
      )}
    </>
  );
}

/** The Overview tab's own part: the project's ids in other systems (custom fields follow). */
function Overview({ project }: { project: ProjectView }) {
  const external = Object.entries(project.externalIds);
  return (
    <div className="flex flex-col gap-5 text-sm" data-testid="project-overview">
      {external.length > 0 && (
        <div className="bg-card border-border rounded-card shadow-card mb-5 border p-5">
          <p className="text-muted-foreground mb-2 text-xs font-semibold">External ids</p>
          <ul>
            {external.map(([system, id]) => (
              <li key={system}>
                {system}: <span className="font-mono">{id}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
